import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { paymentService } from '../services/paymentService';
import { getSubscriptionStatus, completeSubscription } from '../services/onboardingService';
import { fetchBrandDetails } from '../features/brandDetailsSlice';
import { checkOnboardingStage } from '../features/user/userSlice';
import styles from './PaymentSuccess.module.css';

function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector(state => state.user);
  const [status, setStatus] = useState('verifying'); // verifying, success, error, timeout
  const [message, setMessage] = useState('Verifying your payment...');
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [userBrandId, setUserBrandId] = useState(null);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);

  const verifyPaymentWithPolling = useCallback(async (transactionId) => {
    const maxAttempts = 15; // Poll for up to 30 seconds (15 attempts * 2 seconds)
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      setVerificationAttempts(attempt + 1);
      
      try {
        console.log(`Payment verification attempt ${attempt + 1}/${maxAttempts} for transaction:`, transactionId);
        
        const response = await paymentService.getPaymentStatus(transactionId, token);
        console.log('Payment verification response:', response);        
        if (response.status === 'success') {
          setStatus('success');
          
          // Different messages based on payment type
          if (transactionDetails?.type === 'admin') {
            setMessage('Payment successful! Your account has been created and subscription activated.');
          } else {
            setMessage('Payment successful! Your subscription has been activated.');
          }
          
          setTransactionDetails(prev => ({ ...prev, ...response }));
          
          // Clear pending transaction data
          localStorage.removeItem('pending_transaction_id');
          localStorage.removeItem('pending_plan_name');
          
          // Refresh brand details to show new subscription
          if (userBrandId) {
            dispatch(fetchBrandDetails(userBrandId));
          }

          // Check if this is during onboarding flow and complete it
          handleOnboardingCompletion();
          
          return 'verified';
          
        } else if (response.status === 'failed') {
          setStatus('error');
          setMessage('Payment failed. No amount has been debited from your account.');
          setTransactionDetails(prev => ({ ...prev, ...response }));
          return 'failed';
          
        } else if (response.status === 'pending') {
          setMessage(`Verifying your payment... (${attempt + 1}/${maxAttempts})`);
          // Continue polling
        }
        
        // Wait 2 seconds before next attempt
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
      } catch (error) {
        console.error(`Verification attempt ${attempt + 1} failed:`, error);
        
        if (attempt === maxAttempts - 1) {
          // Last attempt failed
          setStatus('timeout');
          setMessage('Payment verification timed out. Please check your subscription status or contact support.');
          return 'timeout';
        }
        
        // Wait before retrying
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }
    }
    
    // If we get here, all attempts failed
    setStatus('timeout');
    setMessage('Payment verification timed out. Please check your subscription status or contact support.');
    return 'timeout';
  }, [token, userBrandId, dispatch]);

  const handleOnboardingCompletion = useCallback(async () => {
    if (!token) return;

    // Check if this is an onboarding payment
    const paymentContext = localStorage.getItem('payment_context');
    
    if (paymentContext !== 'onboarding') {
      console.log('PaymentSuccess: Not an onboarding payment, skipping onboarding completion');
      return;
    }

    try {
      setCompletingOnboarding(true);
      console.log('PaymentSuccess: Starting onboarding completion for onboarding payment');
      
      // Check if user has subscription
      const subscriptionStatus = await getSubscriptionStatus(token);
      
      if (subscriptionStatus.has_subscription) {
        // Complete onboarding
        await completeSubscription(token);
        
        // Re-check onboarding stage
        await dispatch(checkOnboardingStage());
        
        setOnboardingCompleted(true);
        setMessage('Payment successful! Your onboarding is now complete. Welcome to Suspire!');
        
        // Clear payment context since onboarding is complete
        localStorage.removeItem('payment_context');
        
        // Navigate to main app after a delay
        setTimeout(() => {
          navigate('/onboarding'); // This will redirect to welcome screen based on onboarding stage
        }, 3000);
      }
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Don't show error for onboarding completion, payment was successful
    } finally {
      setCompletingOnboarding(false);
    }
  }, [token, dispatch, navigate]);

  useEffect(() => {
    // Get query params from URL
    const txnid = searchParams.get('txnid');
    const statusParam = searchParams.get('status');
    const amount = searchParams.get('amount');
    const type = searchParams.get('type');
    const linkId = searchParams.get('link_id');

    // If status is present in query params, show result immediately (public payment)
    if (txnid && statusParam) {
      setTransactionDetails({ txnid, status: statusParam, amount, type, linkId });
      if (statusParam === 'success') {
        setStatus('success');
        setMessage('Payment successful! Thank you for your payment.');
        // Fetch more details if plan/amount missing
        paymentService.getPaymentDetails && paymentService.getPaymentDetails(txnid).then(details => {
          if (details) {
            setTransactionDetails(prev => ({ ...prev, ...details }));
          }
        }).catch(err => {
          console.warn('Could not fetch extra payment details:', err);
        });
      } else {
        setStatus('error');
        setMessage('Payment failed. No amount has been debited from your account.');
      }
      return;
    }

    // Fetch user profile to get brand_id
    if (token) {
      fetch(process.env.REACT_APP_API_BASE_URL + '/api/auth/users/', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          // NEW API FORMAT: data.user_type, data.user.brand.id
          if (data && data.user && data.user.brand && data.user.brand.id) {
            console.log('User brand_id for payment success:', data.user.brand.id);
            setUserBrandId(data.user.brand.id);
          }
        })
        .catch(err => {
          console.error('Error fetching user profile for brand_id:', err);
        });
    }

    // Extract transaction details from URL parameters (sent by backend webhook)
    const urlParams = {
      transactionId: searchParams.get('txnid'),
      paymentStatus: searchParams.get('status'),
      amount: searchParams.get('amount'),
      paymentType: searchParams.get('type'), // 'admin' for admin payment links, 'subscription' for regular
      linkId: searchParams.get('link_id'), // Only present for admin payment links
      brandName: searchParams.get('brand_name'), // Only present for admin payment links
      customerEmail: searchParams.get('customer_email') // Only present for admin payment links
    };

    console.log('Payment success page loaded with params:', urlParams);

    if (!urlParams.transactionId) {
      setStatus('error');
      setMessage('Transaction ID not found. Please contact support.');
      return;
    }

    // Set initial transaction details from URL
    setTransactionDetails({
      transaction_id: urlParams.transactionId,
      amount: urlParams.amount,
      status: urlParams.paymentStatus,
      type: urlParams.paymentType,
      link_id: urlParams.linkId,
      brand_name: urlParams.brandName,
      customer_email: urlParams.customerEmail
    });

    // Start verification polling for double-confirmation
    verifyPaymentWithPolling(urlParams.transactionId);
  }, [searchParams, token, dispatch, verifyPaymentWithPolling]);

  const handleGoToSubscriptions = () => {
    // Check if this was an onboarding payment
    const paymentContext = localStorage.getItem('payment_context');
    
    if (paymentContext === 'onboarding') {
      console.log('PaymentSuccess: Onboarding payment detected, staying in onboarding flow');
      navigate('/onboarding');
    } else {
      navigate('/subscriptions');
    }
  };

  const handleGoToDashboard = () => {
    navigate('/profile');
  };

  const handleTryAgain = () => {
    // Check if this was an onboarding payment
    const paymentContext = localStorage.getItem('payment_context');
    
    if (paymentContext === 'onboarding') {
      console.log('PaymentSuccess: Onboarding payment retry, staying in onboarding flow');
      navigate('/onboarding');
    } else {
      navigate('/subscriptions');
    }
  };

  const handleContactSupport = () => {
    // You can implement contact support functionality here
    window.open('mailto:support@suspire.com?subject=Payment Verification Issue&body=Transaction ID: ' + transactionDetails?.transaction_id, '_blank');
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {status === 'verifying' && (
          <>
            <div className={styles.loadingIcon}>
              <div className={styles.spinner}></div>
            </div>
            <h1>Verifying Payment</h1>
            <p>{message}</p>
            <div className={styles.progressInfo}>
              <small>Verification attempt: {verificationAttempts}/15</small>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className={styles.successIcon}>✓</div>
            <h1>Payment Successful!</h1>
            <p>{message}</p>
            
            {/* Onboarding completion status */}
            {completingOnboarding && (
              <div className={styles.onboardingStatus}>
                <div className={styles.spinner}></div>
                <p>Completing your onboarding...</p>
              </div>
            )}
            
            {onboardingCompleted && (
              <div className={styles.onboardingComplete}>
                <div className={styles.celebrationIcon}>🎉</div>
                <h3>Welcome to Suspire!</h3>
                <p>Your onboarding is now complete. Redirecting to your dashboard...</p>
              </div>
            )}
            
            {transactionDetails?.type === 'admin' && (
              <div className={styles.adminSuccessInfo}>
                <h4>🎉 Welcome to Suspire!</h4>
                <p>
                  Your account has been automatically created and your subscription is now active. 
                  You can login using the email address: <strong>{transactionDetails.customer_email}</strong>
                </p>
                <p>
                  <small>A welcome email with your login credentials has been sent to your email address.</small>
                </p>
              </div>
            )}
            
            {transactionDetails && (
              <>
                {console.log('PaymentSuccess debug:', { transactionDetails, amountParam: searchParams.get('amount') })}
                <div className={styles.details}>
                  <h3>Payment Receipt</h3>
                  {/* Show Brand and Account Email for admin type only */}
                  {transactionDetails.type === 'admin' && transactionDetails.brand_name && (
                    <div className={styles.detailRow}>
                      <span>Brand:</span>
                      <span>{transactionDetails.brand_name}</span>
                    </div>
                  )}
                  {transactionDetails.type === 'admin' && transactionDetails.customer_email && (
                    <div className={styles.detailRow}>
                      <span>Account Email:</span>
                      <span>{transactionDetails.customer_email}</span>
                    </div>
                  )}
                  {/* Always show Plan, Amount, and Transaction ID if available */}
                  <div className={styles.detailRow}>
                    <span>Plan:</span>
                    <span>{transactionDetails.plan?.name || transactionDetails.plan_name || transactionDetails.plan || 'Subscription Plan'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span>Amount:</span>
                    <span>₹{transactionDetails.amount || searchParams.get('amount') || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span>Transaction ID:</span>
                    <span>{transactionDetails.transaction_id || transactionDetails.txnid || searchParams.get('txnid') || '-'}</span>
                  </div>
                  <div className={styles.detailRow}>
                    <span>Payment Type:</span>
                    <span>{transactionDetails.type === 'admin' ? 'Admin Payment Link' : 'Online Payment'}</span>
                  </div>
                </div>
              </>
            )}
            
            <div className={styles.actions}>
              {transactionDetails?.type === 'admin' ? (
                <>
                  <button 
                    className={styles.primaryButton}
                    onClick={() => navigate('/login')}
                  >
                    Login to Your Account
                  </button>
                  <button 
                    className={styles.secondaryButton}
                    onClick={handleContactSupport}
                  >
                    Contact Support
                  </button>
                </>
              ) : (
                <>
                  {/* Check if this is an onboarding payment */}
                  {localStorage.getItem('payment_context') === 'onboarding' ? (
                    <>
                      <button 
                        className={styles.primaryButton}
                        onClick={() => navigate('/onboarding')}
                      >
                        Continue to Welcome Screen
                      </button>
                      <button 
                        className={styles.secondaryButton}
                        onClick={handleContactSupport}
                      >
                        Contact Support
                      </button>
                    </>
                  ) : (
                    <>
                      <button 
                        className={styles.primaryButton}
                        onClick={handleGoToSubscriptions}
                      >
                        View My Subscriptions
                      </button>
                      <button 
                        className={styles.secondaryButton}
                        onClick={handleGoToDashboard}
                      >
                        Go to Dashboard
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className={styles.errorIcon}>✗</div>
            <h1>Payment Issue</h1>
            <p>{message}</p>
            
            {transactionDetails && (
              <div className={styles.details}>
                <h3>Transaction Details</h3>
                <div className={styles.detailRow}>
                  <span>Transaction ID:</span>
                  <span>{transactionDetails.transaction_id}</span>
                </div>
                {transactionDetails.amount && (
                  <div className={styles.detailRow}>
                    <span>Amount:</span>
                    <span>${transactionDetails.amount}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className={styles.actions}>
              <button 
                className={styles.primaryButton}
                onClick={handleTryAgain}
              >
                Try Again
              </button>
              <button 
                className={styles.secondaryButton}
                onClick={handleContactSupport}
              >
                Contact Support
              </button>
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <div className={styles.warningIcon}>⚠</div>
            <h1>Verification Timeout</h1>
            <p>{message}</p>
            
            {transactionDetails && (
              <div className={styles.details}>
                <h3>Transaction Details</h3>
                <div className={styles.detailRow}>
                  <span>Transaction ID:</span>
                  <span>{transactionDetails.transaction_id}</span>
                </div>
                <div className={styles.detailRow}>
                  <span>Amount:</span>
                  <span>${transactionDetails.amount}</span>
                </div>
              </div>
            )}
            
            <div className={styles.actions}>
              <button 
                className={styles.primaryButton}
                onClick={handleGoToSubscriptions}
              >
                Check Subscription Status
              </button>
              <button 
                className={styles.secondaryButton}
                onClick={handleContactSupport}
              >
                Contact Support
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;
